/**
 * Staff — Training Revenue Report.
 *
 * Date-range picker over `/api/staff/training-report`. Shows total trainees,
 * revenue (net + gross), new bookings (deduped by booking_group_id),
 * discounts given, and free City & Guilds certificates awarded.
 */
import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Home, BarChart3, Users, PoundSterling, Percent, Award,
  Loader2, Calendar as CalendarIcon, Wallet,
} from 'lucide-react';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { BRAND } from '@/pages/trainingBrand';

const GREEN = BRAND.green;

function isoToday() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}
function isoNDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function StatTile({ icon: Icon, label, value, sub, tone = 'default', testid }) {
  const tones = {
    default: 'text-gray-900',
    good: 'text-emerald-700',
    warn: 'text-amber-700',
    bad: 'text-rose-700',
    info: 'text-sky-700',
  };
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-gray-500 mb-1">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        {label}
      </div>
      <div className={`text-2xl font-bold ${tones[tone] || tones.default}`} data-testid={testid}>
        {value}
      </div>
      {sub && <div className="text-[11px] text-gray-500 mt-1">{sub}</div>}
    </Card>
  );
}

export default function TrainingReport() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [start, setStart] = useState(isoNDaysAgo(30));
  const [end, setEnd] = useState(isoToday());
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!user) return;
    if (!['admin', 'manager', 'team'].includes(user.role)) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  const run = async () => {
    if (!start || !end) {
      toast.error('Pick both dates first.');
      return;
    }
    setBusy(true);
    try {
      const qs = new URLSearchParams({ start_date: start, end_date: end });
      const res = await fetch(`${API_URL}/api/staff/training-report?${qs}`, { credentials: 'include' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Report failed');
      }
      setData(await res.json());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (user && ['admin', 'manager', 'team'].includes(user.role)) run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const setRange = (days) => {
    setStart(isoNDaysAgo(days));
    setEnd(isoToday());
  };

  return (
    <div className="min-h-screen" style={{ background: `linear-gradient(180deg, ${GREEN.tint} 0%, #FFFFFF 300px)` }}>
      <div className="border-b sticky top-0 z-20 shadow-sm" style={{ background: GREEN.primary, color: GREEN.ink }}>
        <div className="max-w-5xl mx-auto px-3 sm:px-4 py-3 flex items-center gap-2 sm:gap-3">
          <Link to="/staff/training-courses" className="text-white/80 hover:text-white" data-testid="training-report-back">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <Link to="/" className="text-white/80 hover:text-white hidden sm:inline-flex">
            <Home className="w-5 h-5" />
          </Link>
          <div className="w-8 h-8 rounded-lg bg-white/15 text-white flex items-center justify-center">
            <BarChart3 className="w-4 h-4" />
          </div>
          <h1 className="text-base sm:text-lg font-bold text-white truncate flex-1">Training Revenue Report</h1>
          <Link to="/staff/training-analytics" className="text-xs text-white/80 hover:text-white underline shrink-0" data-testid="training-analytics-link">Analytics &amp; profit</Link>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-4">
        <Card className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[9rem]">
              <Label className="text-xs">Start date</Label>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="bg-white" data-testid="report-start" />
            </div>
            <div className="flex-1 min-w-[9rem]">
              <Label className="text-xs">End date</Label>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="bg-white" data-testid="report-end" />
            </div>
            <Button onClick={run} disabled={busy} className="text-white font-bold" style={{ background: GREEN.primary }} data-testid="report-run">
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CalendarIcon className="w-4 h-4 mr-1.5" />}
              {busy ? 'Loading…' : 'Run report'}
            </Button>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {[7, 30, 90, 365].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setRange(d)}
                className="text-[11px] px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50"
                data-testid={`report-range-${d}`}
              >
                Last {d} days
              </button>
            ))}
          </div>
        </Card>

        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatTile icon={Users} label="Trainees" value={data.trainees_count} sub={`${data.registrations_count} registration${data.registrations_count === 1 ? '' : 's'}`} testid="stat-trainees" />
              <StatTile icon={CalendarIcon} label="New bookings" value={data.new_bookings_count} sub="Deduped multi-course" tone="info" testid="stat-bookings" />
              <StatTile icon={PoundSterling} label="Revenue (net)" value={`£${data.revenue_net.toFixed(2)}`} sub={`Before discount £${data.revenue_gross_before_discount.toFixed(2)}`} tone="good" testid="stat-revenue" />
              <StatTile icon={Percent} label="Discounts given" value={`£${data.discounts_total.toFixed(2)}`} sub={`${data.discounted_trainees_count} trainee${data.discounted_trainees_count === 1 ? '' : 's'}`} tone="warn" testid="stat-discounts" />
            </div>
            <Card className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <Award className="w-5 h-5 text-amber-500" />
                <h2 className="font-bold text-gray-900">Free City &amp; Guilds certificates</h2>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <div className="text-xs uppercase tracking-wider text-gray-500">Awarded</div>
                  <div className="text-2xl font-bold text-gray-900" data-testid="stat-cg-count">{data.free_city_guilds_count}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-gray-500">Retail value lost</div>
                  <div className="text-2xl font-bold text-rose-700" data-testid="stat-cg-retail">£{data.free_city_guilds_retail_lost.toFixed(2)}</div>
                  <div className="text-[11px] text-gray-500">@ £{data.settings_snapshot.city_guilds_retail_gross_each.toFixed(2)} each</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-gray-500">Cost incurred</div>
                  <div className="text-2xl font-bold text-amber-700" data-testid="stat-cg-cost">£{data.free_city_guilds_cost_incurred.toFixed(2)}</div>
                  <div className="text-[11px] text-gray-500">@ £{data.settings_snapshot.city_guilds_cost_gross_each.toFixed(2)} each</div>
                </div>
              </div>
            </Card>

            {/* Payments by method — channel-mix within the selected date
                range. Sums payment_history entries whose `at` falls INSIDE
                the window, not the registration date, so this shows real
                cashflow-in-period. A trainee booked in April paying the
                balance in September shows up in a September report. */}
            <Card className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <Wallet className="w-5 h-5 text-indigo-500" />
                <h2 className="font-bold text-gray-900">Payments received — by method</h2>
                <span className="ml-auto text-xs font-semibold text-gray-500" data-testid="stat-payments-total">
                  Total £{Number(data.payments_total_in_range || 0).toFixed(2)}
                </span>
              </div>
              {(data.payments_by_method || []).length === 0 ? (
                <p className="text-sm text-gray-500 italic" data-testid="payments-by-method-empty">
                  No payments received in this window. Log payments on the register or record them via the Add-a-Payment popover to see the channel mix build up.
                </p>
              ) : (
                <ol className="space-y-2" data-testid="payments-by-method-list">
                  {data.payments_by_method.map((m, idx) => {
                    const grand = Number(data.payments_total_in_range || 0);
                    const pct = grand > 0 ? Math.round((m.total / grand) * 1000) / 10 : 0;
                    // Colour per method — matches the popover source badges
                    // spiritually (kept subtle so a busy report doesn't
                    // scream). Falls back to slate for any custom method.
                    const tone = m.method === 'Bank Transfer' ? 'bg-sky-500'
                      : m.method === 'Cash' ? 'bg-amber-500'
                      : m.method.startsWith('Stripe') ? 'bg-violet-500'
                      : m.method.startsWith('Worldpay') ? 'bg-emerald-500'
                      : 'bg-slate-500';
                    return (
                      <li key={`${m.method}-${idx}`} className="text-sm" data-testid={`payments-by-method-row-${idx}`}>
                        <div className="flex items-baseline justify-between gap-3">
                          <span className="font-semibold text-gray-900 truncate">{m.method}</span>
                          <span className="tabular-nums text-gray-900 font-bold" data-testid={`payments-by-method-total-${idx}`}>
                            £{Number(m.total).toFixed(2)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                            <div
                              className={`h-full ${tone} transition-all`}
                              style={{ width: `${Math.max(2, pct)}%` }}
                              title={`${pct}% of payments in this window`}
                            />
                          </div>
                          <span className="text-[11px] text-gray-500 tabular-nums w-24 text-right">
                            {pct}% · {m.count} payment{m.count === 1 ? '' : 's'}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
